import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { VERSION_INFO } from '../constants/version';
import { getErrorMessage } from '../utils/errorHandler';
import JournalHistorySidePanel from './JournalHistorySidePanel';
import KamokuSelect from './KamokuSelect';
import HojoSelect from './HojoSelect';
import DateInput from './DateInput';
import { notifyJournalListShouldRefresh } from '../utils/journalListSync';

interface JournalSuggestion {
  id: number;
  tekiyou: string;
  karikata_kamoku_id?: number | null;
  karikata_kamoku_name: string;
  kashikata_kamoku_id?: number | null;
  kashikata_kamoku_name: string;
  karikata_kingaku: number;
  kashikata_kingaku: number;
  kihyoubi: string;
  similarity_score?: number;
  frequency_score?: number;
  recency_score?: number;
  total_score?: number;
}

interface Kamoku {
  id: number;
  kamoku_code: string;
  kamoku: string;
  kamoku_yomi?: string | null;
  hojo_kamoku_ari?: boolean;
}

interface JournalLine {
  id: string;
  date: string;  // 行ごとの日付（空の場合は共通起票日を使用）
  debitAccountId: number | null;
  debitHojoId: number | null;  // 借方補助科目ID
  creditAccountId: number | null;
  creditHojoId: number | null;  // 貸方補助科目ID
  amount: number;
  description: string;
}

interface ClassicJournalEntryProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  selectedYear?: number;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const generateLineId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `line-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createEmptyLine = (defaultDate: string = ''): JournalLine => ({
  id: generateLineId(),
  date: defaultDate,
  debitAccountId: null,
  debitHojoId: null,
  creditAccountId: null,
  creditHojoId: null,
  amount: 0,
  description: '',
});

const getToday = () => new Date().toISOString().split('T')[0];

/** 対象年度（4/1〜翌3/31）の範囲内かどうか */
function isDateInFiscalYear(dateStr: string, year: number | undefined): boolean {
  if (!year || !dateStr || dateStr.length < 10) return true;
  const start = `${year}-04-01`;
  const end = `${year + 1}-03-31`;
  return dateStr >= start && dateStr <= end;
}

const ClassicJournalEntry: React.FC<ClassicJournalEntryProps> = ({
  onMenuSelect,
  tenantName,
  selectedYear,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  // Force update
  const [kamokuList, setKamokuList] = useState<Kamoku[]>([]);
  const [kihyoubi, setKihyoubi] = useState<string>(() => localStorage.getItem('lastSavedDate') || getToday());
  const [comment, setComment] = useState('');
  const [lines, setLines] = useState<JournalLine[]>(() => [createEmptyLine()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<JournalSuggestion[]>([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [selectedHistoryAccountId, setSelectedHistoryAccountId] = useState<number | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  /** 保存成功後、1行目の「日」入力へフォーカスを戻す（ヘッダー年度へ飛ぶのを防ぐ） */
  const firstLineDayInputRef = useRef<HTMLInputElement>(null);

  const fiscalStart = selectedYear ? `${selectedYear}-04-01` : '';
  const fiscalEnd = selectedYear ? `${selectedYear + 1}-03-31` : '';

  const kamokuMap = useMemo(() => {
    const map = new Map<number, Kamoku>();
    kamokuList.forEach((kamoku) => map.set(kamoku.id, kamoku));
    return map;
  }, [kamokuList]);

  const activeLine = activeLineIndex !== null ? lines[activeLineIndex] ?? null : null;
  const activeLineDescription = activeLine?.description ?? '';
  const activeDebitName =
    activeLine?.debitAccountId != null
      ? kamokuMap.get(activeLine.debitAccountId)?.kamoku ?? ''
      : '';
  const activeCreditName =
    activeLine?.creditAccountId != null
      ? kamokuMap.get(activeLine.creditAccountId)?.kamoku ?? ''
      : '';

  useEffect(() => {
    if (currentTenantId == null) {
      setKamokuList([]);
      return;
    }

    const fetchKamoku = async () => {
      try {
        const response = await apiFetch('/api/kamoku');
        if (!response.ok) {
          const errorMessage = await getErrorMessage(response, '勘定科目の取得に失敗しました');
          throw new Error(errorMessage);
        }
        const data = await response.json();
        setKamokuList(data);
      } catch (err: any) {
        console.error('勘定科目取得エラー:', err);
        setError(err?.message || '勘定科目の取得に失敗しました');
      }
    };

    fetchKamoku();
  }, [apiFetch, currentTenantId]);

  useEffect(() => {
    if (activeLineIndex === null) {
      setSuggestionQuery('');
      return;
    }
    setSuggestionQuery(activeLineDescription.trim());
  }, [activeLineDescription, activeLineIndex]);

  useEffect(() => {
    if (activeLineIndex === null) {
      setSuggestions([]);
      setSuggestionError(null);
      setSuggestionLoading(false);
      return;
    }

    let isCancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        setSuggestionLoading(true);
        setSuggestionError(null);
        const params = new URLSearchParams({
          query: suggestionQuery,
          limit: suggestionQuery ? '20' : '30',
        });
        if (selectedYear) params.append('fiscal_year', selectedYear.toString());
        const response = await apiFetch(`/api/journal-suggestions?${params.toString()}`);
        if (!response.ok) {
          const errorMessage = await getErrorMessage(response, '候補の取得に失敗しました');
          throw new Error(errorMessage);
        }
        const data = await response.json();
        if (!isCancelled) {
          setSuggestions(data.suggestions || []);
        }
      } catch (err: any) {
        if (!isCancelled) {
          console.error('候補取得エラー:', err);
          setSuggestionError(err?.message || '候補の取得に失敗しました');
          setSuggestions([]);
        }
      } finally {
        if (!isCancelled) {
          setSuggestionLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiFetch, suggestionQuery, activeLineIndex, currentTenantId, selectedYear]);

  const validLines = useMemo(
    () =>
      lines.filter(
        (line) =>
          line.debitAccountId !== null &&
          line.creditAccountId !== null &&
          line.amount > 0,
      ),
    [lines],
  );

  const totalAmount = useMemo(
    () => validLines.reduce((sum, line) => sum + line.amount, 0),
    [validLines],
  );

  const totalDebit = totalAmount;
  const totalCredit = totalAmount;
  const balanceDiff = totalDebit - totalCredit;
  const isBalanced = totalDebit > 0 && balanceDiff === 0;

  const classicButtonStyle: React.CSSProperties = {
    padding: '8px 20px',
    backgroundColor: '#e0e0e0',
    border: '2px outset #ffffff',
    fontSize: '15px',
    fontWeight: 'bold',
    fontFamily: 'MS UI Gothic, sans-serif',
    cursor: 'pointer',
    margin: '3px',
    borderRadius: '4px',
    boxShadow: '1px 1px 2px rgba(0,0,0,0.2)'
  };

  const classicInputStyle: React.CSSProperties = {
    padding: '4px 6px',
    border: '2px inset #c0c0c0',
    fontSize: '14px',
    fontFamily: 'MS UI Gothic, sans-serif',
    backgroundColor: 'white',
    height: '32px', // Increased height
    boxSizing: 'border-box',
  };

  // スピンボタン非表示用CSS
  const numberInputStyle: React.CSSProperties = {
    ...classicInputStyle,
    MozAppearance: 'textfield', // Firefox
    WebkitAppearance: 'none', // Chrome/Safari/Edge
  };

  const classicSelectStyle: React.CSSProperties = {
    padding: '2px',
    border: '2px inset #c0c0c0',
    fontSize: '12px',
    fontFamily: 'MS UI Gothic, sans-serif',
    backgroundColor: 'white',
    height: '32px', // Increased height
    boxSizing: 'border-box',
  };

  const kamokuNameMap = useMemo(() => {
    const map = new Map<string, Kamoku>();
    kamokuList.forEach((kamoku) => map.set(kamoku.kamoku, kamoku));
    return map;
  }, [kamokuList]);

  const historyAccountOptions = useMemo(() => {
    const options: Array<{ value: number | null; label: string }> = [
      { value: null, label: '全ての科目' },
    ];

    const seen = new Set<number>();
    const pushOption = (accountId: number | null) => {
      if (accountId == null) {
        return;
      }
      if (seen.has(accountId)) {
        return;
      }
      const kamoku = kamokuMap.get(accountId);
      if (!kamoku) {
        return;
      }
      seen.add(accountId);
      options.push({
        value: kamoku.id,
        label: `${kamoku.kamoku_code} - ${kamoku.kamoku}`,
      });
    };

    if (activeLine) {
      pushOption(activeLine.debitAccountId);
      pushOption(activeLine.creditAccountId);
    }

    suggestions.forEach((suggestion) => {
      const debitKamoku = kamokuNameMap.get(suggestion.karikata_kamoku_name);
      const creditKamoku = kamokuNameMap.get(suggestion.kashikata_kamoku_name);
      pushOption(debitKamoku?.id ?? null);
      pushOption(creditKamoku?.id ?? null);
    });

    const [allOption, ...rest] = options;
    rest.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    return [allOption, ...rest];
  }, [activeLine, kamokuMap, kamokuNameMap, suggestions]);

  useEffect(() => {
    const availableIds = historyAccountOptions
      .map((option) => option.value)
      .filter((value): value is number => value !== null);

    if (availableIds.length === 0) {
      if (selectedHistoryAccountId !== null) {
        setSelectedHistoryAccountId(null);
      }
      return;
    }

    if (
      selectedHistoryAccountId !== null &&
      !availableIds.includes(selectedHistoryAccountId)
    ) {
      setSelectedHistoryAccountId(availableIds[0]);
      return;
    }

    if (selectedHistoryAccountId === null && activeLine) {
      if (activeLine.debitAccountId && availableIds.includes(activeLine.debitAccountId)) {
        setSelectedHistoryAccountId(activeLine.debitAccountId);
        return;
      }
      if (activeLine.creditAccountId && availableIds.includes(activeLine.creditAccountId)) {
        setSelectedHistoryAccountId(activeLine.creditAccountId);
      }
    }
  }, [activeLine, historyAccountOptions, selectedHistoryAccountId]);

  const filteredSuggestions = useMemo(() => {
    const sorted = [...suggestions].sort((a, b) => {
      if (a.kihyoubi === b.kihyoubi) {
        const scoreA = a.total_score ?? 0;
        const scoreB = b.total_score ?? 0;
        return scoreB - scoreA;
      }
      return a.kihyoubi < b.kihyoubi ? 1 : -1;
    });

    if (selectedHistoryAccountId === null) {
      return sorted;
    }

    const targetKamoku = kamokuMap.get(selectedHistoryAccountId);
    if (!targetKamoku) {
      return sorted;
    }

    return sorted.filter(
      (suggestion) =>
        suggestion.karikata_kamoku_name === targetKamoku.kamoku ||
        suggestion.kashikata_kamoku_name === targetKamoku.kamoku,
    );
  }, [kamokuMap, selectedHistoryAccountId, suggestions]);

  const selectedHistoryAccountName =
    selectedHistoryAccountId != null ? kamokuMap.get(selectedHistoryAccountId)?.kamoku ?? '' : '';

  const historyContextNote = useMemo(() => {
    if (!activeLine) {
      return '仕訳行を選択すると履歴候補が表示されます。';
    }
    const parts: string[] = [];
    if (selectedHistoryAccountName) {
      parts.push(`表示科目: ${selectedHistoryAccountName}`);
    }
    if (activeDebitName) {
      parts.push(`借方: ${activeDebitName}`);
    }
    if (activeCreditName) {
      parts.push(`貸方: ${activeCreditName}`);
    }
    if (parts.length === 0) {
      return '摘要や科目の入力に応じて過去の仕訳履歴を表示します。';
    }
    return parts.join(' / ');
  }, [activeLine, activeDebitName, activeCreditName, selectedHistoryAccountName]);

  const updateLine = (index: number, updates: Partial<JournalLine>) => {
    setLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, ...updates } : line)));
  };

  const handleAccountChange = (
    index: number,
    field: 'debitAccountId' | 'creditAccountId',
    kamokuId: string,
  ) => {
    const accountId = kamokuId === '' ? null : Number(kamokuId);
    const hojoField = field === 'debitAccountId' ? 'debitHojoId' : 'creditHojoId';
    // 科目変更時は補助科目をリセット
    updateLine(index, { [field]: accountId, [hojoField]: null } as Partial<JournalLine>);
    setActiveLineIndex(index);
  };

  const handleHojoChange = (
    index: number,
    field: 'debitHojoId' | 'creditHojoId',
    hojoId: string,
  ) => {
    const hojoIdValue = hojoId === '' ? null : Number(hojoId);
    updateLine(index, { [field]: hojoIdValue } as Partial<JournalLine>);
    setActiveLineIndex(index);
  };

  const handleAmountChange = (index: number, value: string) => {
    const parsed = parseInt(value, 10);
    const amount = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    updateLine(index, { amount });
    setActiveLineIndex(index);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    updateLine(index, { description: value });
    setActiveLineIndex(index);
    setSuggestionQuery(value.trim());
  };

  const updateDateRangeError = useCallback(() => {
    if (selectedYear === undefined) {
      setDateRangeError(null);
      return;
    }
    const rangeLabel = `${selectedYear}年4月1日〜${selectedYear + 1}年3月31日`;
    if (kihyoubi && !isDateInFiscalYear(kihyoubi, selectedYear)) {
      setDateRangeError(`起票日「${kihyoubi}」は対象年度（${rangeLabel}）の範囲外です。`);
      return;
    }
    const outOfRangeLine = lines.find((line) => line.date && !isDateInFiscalYear(line.date, selectedYear));
    if (outOfRangeLine) {
      setDateRangeError(`行の日付「${outOfRangeLine.date}」は対象年度（${rangeLabel}）の範囲外です。`);
      return;
    }
    setDateRangeError(null);
  }, [selectedYear, kihyoubi, lines]);

  useEffect(() => {
    updateDateRangeError();
  }, [updateDateRangeError]);

  const handleDateChange = (index: number, value: string) => {
    updateLine(index, { date: value });
    setActiveLineIndex(index);
    setKihyoubi(value);
  };

  const handleDescriptionFocus = (index: number, value: string) => {
    setActiveLineIndex(index);
    setSuggestionQuery(value.trim());
  };

  const handleDescriptionBlur = () => {
    // keep suggestion panel visible
  };

  const handleAddLine = () => {
    // 前の行の日付を引き継ぐ
    const lastLine = lines[lines.length - 1];
    const defaultDate = lastLine?.date || '';
    const nextLines = [...lines, createEmptyLine(defaultDate)];
    setLines(nextLines);
    setActiveLineIndex(nextLines.length - 1);
    setSuggestionQuery('');
  };

  // DOMベースのEnterキーナビゲーション (ユーザー提案の改善版)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (e.nativeEvent.isComposing) return; // IME変換中は無視

    e.preventDefault();

    const target = e.target as HTMLElement;
    const inputs = Array.from(
      tableContainerRef.current?.querySelectorAll('input:not([type="hidden"]):not([disabled])') || []
    ) as HTMLElement[];

    const currentIndex = inputs.indexOf(target);

    if (currentIndex === -1) {
      return;
    }

    if (currentIndex < inputs.length - 1) {
      inputs[currentIndex + 1].focus();
    } else {
      const td = target.closest('td');
      const fieldType = td?.getAttribute('data-field-type');

      if (fieldType === 'description') {
        handleAddLine();
        setTimeout(() => {
          const newInputs = Array.from(
            tableContainerRef.current?.querySelectorAll('input:not([type="hidden"]):not([disabled])') || []
          ) as HTMLElement[];
          if (newInputs.length > currentIndex + 1) {
            newInputs[currentIndex + 1].focus();
          }
        }, 100);
      }
    }
  }, [handleAddLine]);



  const handleRemoveLine = (lineId: string) => {
    if (lines.length === 1) {
      setLines([createEmptyLine('')]);
      setActiveLineIndex(0);
      setSuggestionQuery('');
      return;
    }

    const removedIndex = lines.findIndex((line) => line.id === lineId);
    const filtered = lines.filter((line) => line.id !== lineId);
    const finalLines = filtered.length === 0 ? [createEmptyLine()] : filtered;
    setLines(finalLines);

    setActiveLineIndex((prev) => {
      if (prev === null) {
        return prev;
      }
      if (removedIndex === -1) {
        return prev;
      }
      if (prev > removedIndex) {
        return Math.max(0, prev - 1);
      }
      if (prev === removedIndex) {
        const nextIndex = Math.min(finalLines.length - 1, removedIndex);
        setSuggestionQuery(finalLines[nextIndex]?.description.trim() || '');
        return nextIndex;
      }
      return prev;
    });
  };

  const handleSuggestionSelect = (suggestion: any, lineIndex: number) => {
    const karikata = kamokuList.find((k) => k.kamoku === suggestion.karikata_kamoku_name);
    const kashikata = kamokuList.find((k) => k.kamoku === suggestion.kashikata_kamoku_name);

    updateLine(lineIndex, {
      debitAccountId: karikata?.id ?? null,
      creditAccountId: kashikata?.id ?? null,
      description: suggestion.tekiyou || '',
    });

    if (!comment) {
      setComment(suggestion.tekiyou || '');
    }

    setSuggestionQuery((suggestion.tekiyou || '').trim());
  };

  const copyDebitFromAbove = (targetIndex: number | null) => {
    const idx = targetIndex ?? activeLineIndex;
    if (idx === null || idx === 0) return;
    const prevLine = lines[idx - 1];
    updateLine(idx, { debitAccountId: prevLine.debitAccountId });
  };

  const copyCreditFromAbove = (targetIndex: number | null) => {
    const idx = targetIndex ?? activeLineIndex;
    if (idx === null || idx === 0) return;
    const prevLine = lines[idx - 1];
    updateLine(idx, { creditAccountId: prevLine.creditAccountId });
  };

  const copyAllFromAbove = (targetIndex: number | null) => {
    const idx = targetIndex ?? activeLineIndex;
    if (idx === null || idx === 0) return;
    const prevLine = lines[idx - 1];
    updateLine(idx, {
      debitAccountId: prevLine.debitAccountId,
      creditAccountId: prevLine.creditAccountId,
      amount: prevLine.amount,
      description: prevLine.description,
    });
  };

  const copyDescriptionFromAbove = (targetIndex: number | null) => {
    const idx = targetIndex ?? activeLineIndex;
    if (idx === null || idx === 0) return;
    const prevLine = lines[idx - 1];
    updateLine(idx, { description: prevLine.description });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeLineIndex === null || activeLineIndex === 0) return;

      switch (e.key) {
        case 'F2':
          e.preventDefault();
          copyDebitFromAbove(activeLineIndex);
          break;
        case 'F3':
          e.preventDefault();
          copyCreditFromAbove(activeLineIndex);
          break;
        case 'F4':
          e.preventDefault();
          copyAllFromAbove(activeLineIndex);
          break;
        case 'F5':
          e.preventDefault();
          copyDescriptionFromAbove(activeLineIndex);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeLineIndex, lines]);

  const resetForm = (opts?: { activateFirstLine?: boolean }) => {
    const lastDate = localStorage.getItem('lastSavedDate') || getToday();
    setLines([createEmptyLine(lastDate)]);
    setKihyoubi(lastDate);
    setComment('');
    setError(null);
    setActiveLineIndex(opts?.activateFirstLine ? 0 : null);
    setSuggestions([]);
    setSuggestionQuery('');
  };

  const handleSave = async () => {
    const hasPartialLine = lines.some((line) => {
      const hasAnyValue =
        line.debitAccountId !== null ||
        line.creditAccountId !== null ||
        line.amount > 0 ||
        line.description.trim().length > 0;

      if (!hasAnyValue) {
        return false;
      }

      return !(
        line.debitAccountId !== null &&
        line.creditAccountId !== null &&
        line.amount > 0
      );
    });

    if (hasPartialLine) {
      setError('未入力の項目がある仕訳行があります。借方・貸方の勘定科目と金額を入力してください。');
      return;
    }

    if (validLines.length === 0) {
      setError('仕訳行を1行以上入力してください。');
      return;
    }

    if (!isBalanced) {
      setError('借方と貸方の合計金額が一致していません。');
      return;
    }

    if (selectedYear !== undefined) {
      const rangeLabel = `${selectedYear}年4月1日〜${selectedYear + 1}年3月31日`;
      const effectiveKihyoubiForCheck = validLines.find((line) => line.date)?.date || kihyoubi;
      if (effectiveKihyoubiForCheck && !isDateInFiscalYear(effectiveKihyoubiForCheck, selectedYear)) {
        if (!window.confirm(`起票日（${effectiveKihyoubiForCheck}）が対象年度（${rangeLabel}）の範囲外ですが、このまま保存してよろしいですか？`)) {
          return;
        }
      } else {
        const outOfRange = validLines.find((line) => {
          const d = line.date || kihyoubi;
          return d && !isDateInFiscalYear(d, selectedYear);
        });
        if (outOfRange) {
          const d = outOfRange.date || kihyoubi;
          if (!window.confirm(`日付「${d}」が対象年度（${rangeLabel}）の範囲外ですが、このまま保存してよろしいですか？`)) {
            return;
          }
        }
      }
    }

    setLoading(true);
    setError(null);

    // 各行の日付から最初の有効な日付を取得（伝票全体の起票日として使用）
    const effectiveKihyoubi = validLines.find((line) => line.date)?.date || kihyoubi;

    const payload = {
      kihyoubi: effectiveKihyoubi,
      comment,
      lines: validLines.map((line, index) => {
        // 行ごとの日付が空の場合は共通起票日を使用
        const lineDate = line.date || kihyoubi;
        return {
          line_no: index + 1,
          kihyoubi: lineDate,
          debit_account_id: line.debitAccountId,
          debit_hojo_id: line.debitHojoId,
          debit_amount: line.amount,
          credit_account_id: line.creditAccountId,
          credit_hojo_id: line.creditHojoId,
          credit_amount: line.amount,
          description: line.description || comment,
        };
      }),
    };

    try {
      const response = await apiFetch('/api/journal-entries', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '保存に失敗しました');
        setError(errorMessage);
        return;
      }

      localStorage.setItem('lastSavedDate', effectiveKihyoubi);
      notifyJournalListShouldRefresh();
      alert('仕訳を保存しました');
      resetForm({ activateFirstLine: true });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = firstLineDayInputRef.current;
          el?.focus();
          el?.select();
        });
      });
    } catch (err: any) {
      console.error('仕訳保存エラー:', err);
      setError(err?.message || '保存中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        minHeight: '100vh',
        fontFamily: 'MS UI Gothic, sans-serif',
        fontSize: '12px',
      }}
    >
      <div style={{ padding: '16px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#ffcccc',
              border: '1px solid #ff0000',
              padding: '8px',
              marginBottom: '16px',
              fontSize: '12px',
            }}
          >
            {error}
          </div>
        )}
        {dateRangeError && (
          <div
            style={{
              backgroundColor: '#ffcccc',
              border: '1px solid #cc0000',
              padding: '8px',
              marginBottom: '16px',
              fontSize: '12px',
            }}
          >
            {dateRangeError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 62%', minWidth: 0 }}>
            <fieldset
              style={{
                border: '2px inset #c0c0c0',
                backgroundColor: '#ffffff',
                marginBottom: '16px',
                padding: '12px',
              }}
            >
              <legend style={{ fontWeight: 'bold', fontSize: '14px', padding: '0 8px' }}>
                共通情報
              </legend>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px 18px',
                  alignItems: 'center',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '60px' }}>起票日</span>
                  <DateInput
                    value={kihyoubi}
                    onChange={(val) => setKihyoubi(val)}
                    onKeyDown={handleKeyDown}
                    defaultYear={selectedYear || new Date().getFullYear()}
                    style={{ ...classicInputStyle, width: '130px' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 280px' }}>
                  <span style={{ minWidth: '80px' }}>摘要(共通)</span>
                  <input
                    type="text"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={{ ...classicInputStyle, flex: 1 }}
                    placeholder="全体に適用する摘要を入力（任意）"
                  />
                </label>
              </div>
            </fieldset>

            <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontWeight: 'bold', fontSize: '14px', marginRight: '10px' }}>行コピー機能:</span>
              <button onClick={() => copyDebitFromAbove(activeLineIndex)} style={classicButtonStyle} title="F2: 上の行から借方科目をコピー">F2: 借方コピー</button>
              <button onClick={() => copyCreditFromAbove(activeLineIndex)} style={classicButtonStyle} title="F3: 上の行から貸方科目をコピー">F3: 貸方コピー</button>
              <button onClick={() => copyAllFromAbove(activeLineIndex)} style={classicButtonStyle} title="F4: 上の行から全てコピー">F4: 全コピー</button>
              <button onClick={() => copyDescriptionFromAbove(activeLineIndex)} style={classicButtonStyle} title="F5: 上の行から摘要をコピー">F5: 摘要コピー</button>
            </div>

            <div
              ref={tableContainerRef}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #c0c0c0',
                marginBottom: '16px',
                overflowX: 'auto',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e0e0e0' }}>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'center', width: '40px' }}>
                      No
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'center', width: '100px' }}>
                      日付
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', width: '180px' }}>
                      借方(科目/補助)
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', width: '180px' }}>
                      貸方(科目/補助)
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'right', width: '110px' }}>
                      金額
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left' }}>
                      摘要
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'center', width: '60px' }}>
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => {
                    const isActive = index === activeLineIndex;
                    const amountValue = line.amount === 0 ? '' : line.amount;
                    return (
                      <tr
                        key={line.id}
                        style={{
                          backgroundColor: isActive ? '#fff7c2' : '#ffffff',
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <td style={{ padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{index + 1}</td>
                        <td style={{ padding: '6px', verticalAlign: 'middle' }} data-line-index={index} data-field-type="date">
                          <div
                            onFocusCapture={(e) => {
                              const wrap = e.currentTarget;
                              const rel = e.relatedTarget as Node | null;
                              const tgt = e.target as Node;
                              // 年/月/日の間の移動では起票日同期しない（部分入力が消えないようにする）
                              if (rel && wrap.contains(rel) && wrap.contains(tgt)) {
                                return;
                              }
                              setActiveLineIndex(index);
                              handleDateChange(index, kihyoubi);
                            }}
                          >
                            <DateInput
                              autoFocus={index === 0}
                              dayInputRef={index === 0 ? firstLineDayInputRef : undefined}
                              value={line.date}
                              onChange={(val) => handleDateChange(index, val)}
                              onKeyDown={handleKeyDown}
                              defaultYear={selectedYear || new Date().getFullYear()}
                              style={{ ...classicInputStyle, width: '100%' }}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '6px', verticalAlign: 'middle' }} data-line-index={index} data-field-type="debit">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <KamokuSelect
                              kamokuList={kamokuList as any[]}
                              value={line.debitAccountId}
                              onChange={(kamokuId: string) => handleAccountChange(index, 'debitAccountId', kamokuId)}
                              onFocus={() => {
                                setActiveLineIndex(index);
                                if (line.debitAccountId) {
                                  setSelectedHistoryAccountId(line.debitAccountId);
                                }
                              }}
                              style={{ width: '100%', height: '32px' }}
                              onKeyDown={handleKeyDown}
                            />
                            <HojoSelect
                              kamokuId={line.debitAccountId}
                              value={line.debitHojoId}
                              onChange={(hojoId: string) => handleHojoChange(index, 'debitHojoId', hojoId)}
                              apiFetch={apiFetch as any}
                              currentTenantId={currentTenantId || undefined}
                              hasHojo={!!kamokuList.find(k => k.id === line.debitAccountId)?.hojo_kamoku_ari}
                              style={{ width: '100%', height: '32px' }}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '6px', verticalAlign: 'middle' }} data-line-index={index} data-field-type="credit">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <KamokuSelect
                              kamokuList={kamokuList as any[]}
                              value={line.creditAccountId}
                              onChange={(kamokuId: string) => handleAccountChange(index, 'creditAccountId', kamokuId)}
                              onFocus={() => {
                                setActiveLineIndex(index);
                                if (line.creditAccountId) {
                                  setSelectedHistoryAccountId(line.creditAccountId);
                                }
                              }}
                              style={{ width: '100%', height: '32px' }}
                              onKeyDown={handleKeyDown}
                            />
                            <HojoSelect
                              kamokuId={line.creditAccountId}
                              value={line.creditHojoId}
                              onChange={(hojoId: string) => handleHojoChange(index, 'creditHojoId', hojoId)}
                              apiFetch={apiFetch as any}
                              currentTenantId={currentTenantId || undefined}
                              hasHojo={!!kamokuList.find(k => k.id === line.creditAccountId)?.hojo_kamoku_ari}
                              style={{ width: '100%', height: '32px' }}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '6px', textAlign: 'right', verticalAlign: 'middle' }} data-line-index={index} data-field-type="amount">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={amountValue}
                            onChange={(e) => handleAmountChange(index, e.target.value)}
                            onFocus={() => setActiveLineIndex(index)}
                            onKeyDown={handleKeyDown}
                            style={{ ...numberInputStyle, width: '100%', textAlign: 'right' }}
                            placeholder="0"
                          />
                        </td>
                        <td style={{ padding: '6px', verticalAlign: 'middle' }} data-line-index={index} data-field-type="description">
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                            onFocus={(e) => handleDescriptionFocus(index, e.target.value)}
                            onBlur={handleDescriptionBlur}
                            onKeyDown={handleKeyDown}
                            style={{ ...classicInputStyle, width: '100%' }}
                            placeholder="摘要を入力"
                          />
                        </td>
                        <td style={{ padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <button
                            onClick={() => handleRemoveLine(line.id)}
                            style={{ ...classicButtonStyle, padding: '4px 8px', fontSize: '11px' }}
                            disabled={lines.length === 1}
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* "行を追加" button removed as per request */}


            <div
              style={{
                padding: '10px 12px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #c0c0c0',
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 'bold',
              }}
            >
              <span>借方合計: {totalDebit.toLocaleString('ja-JP')} 円</span>
              <span>貸方合計: {totalCredit.toLocaleString('ja-JP')} 円</span>
              <span style={{ color: isBalanced ? '#006400' : '#cc0000' }}>
                差額: {balanceDiff.toLocaleString('ja-JP')} 円
              </span>
            </div>

            <div style={{ marginTop: '18px', display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSave}
                disabled={loading || !isBalanced}
                style={{
                  ...classicButtonStyle,
                  padding: '8px 20px',
                  opacity: loading || !isBalanced ? 0.7 : 1,
                }}
              >
                {loading ? '保存中...' : '保存'}
              </button>
              <button
                onClick={() => resetForm()}
                style={{ ...classicButtonStyle, padding: '8px 20px' }}
              >
                クリア
              </button>
              <button
                onClick={() => onMenuSelect('main-menu')}
                style={{ ...classicButtonStyle, padding: '8px 20px' }}
              >
                メインメニューに戻る
              </button>
            </div>
          </div>

          {/* 右側履歴パネル（タブ切り替え機能付き） */}
          <div
            style={{
              flex: '0 0 34%',
              position: 'sticky',
              top: '92px',
              alignSelf: 'flex-start',
            }}
          >
            <JournalHistorySidePanel
              suggestions={filteredSuggestions}
              suggestionLoading={suggestionLoading}
              suggestionError={suggestionError}
              accountOptions={historyAccountOptions}
              selectedAccountId={selectedHistoryAccountId}
              selectedAccountName={selectedHistoryAccountName}
              onAccountChange={(accountId) => setSelectedHistoryAccountId(accountId)}
              onSuggestionSelect={(suggestion) => {
                if (activeLineIndex !== null) {
                  handleSuggestionSelect(suggestion, activeLineIndex);
                }
              }}
              contextNote={historyContextNote}
              apiFetch={apiFetch}
              currentTenantId={currentTenantId}
              kamokuList={kamokuList}
              activeLineDebitAccountId={activeLine?.debitAccountId}
              activeLineCreditAccountId={activeLine?.creditAccountId}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          right: '15px',
          fontSize: '10px',
          color: '#666',
          fontFamily: 'MS UI Gothic, sans-serif',
        }}
      >
        Ver {VERSION_INFO.version}
      </div>
    </div>
  );
};

export default ClassicJournalEntry;
